import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DiagnosticResult, SpacedRepetitionData } from '../types/diagnostic';
import {
  createInitialRepetition,
  updateRepetition,
  diagnosticResultToQuality,
} from '../engine/spaced-repetition';

interface DiagnosticState {
  /** childId -> DiagnosticResult[] */
  results: Record<string, DiagnosticResult[]>;
  /** childId -> skillId -> SpacedRepetitionData */
  repetitions: Record<string, Record<string, SpacedRepetitionData>>;

  addResults: (childId: string, newResults: DiagnosticResult[]) => void;
  getResults: (childId: string) => DiagnosticResult[];
  getRepetitions: (childId: string) => Record<string, SpacedRepetitionData>;
}

export const useDiagnosticStore = create<DiagnosticState>()(
  persist(
    (set, get) => ({
      results: {},
      repetitions: {},

      addResults: (childId, newResults) => {
        set(state => {
          const existing = state.results[childId] || [];
          const existingReps = state.repetitions[childId] || {};
          const updatedReps = { ...existingReps };

          // Update spaced repetition for each result
          for (const result of newResults) {
            const current = updatedReps[result.skillId] || createInitialRepetition(result.skillId);
            const quality = diagnosticResultToQuality(result.correct, result.responseTimeMs);
            updatedReps[result.skillId] = updateRepetition(current, quality);
          }

          return {
            results: {
              ...state.results,
              [childId]: [...existing, ...newResults],
            },
            repetitions: {
              ...state.repetitions,
              [childId]: updatedReps,
            },
          };
        });
      },

      getResults: (childId) => get().results[childId] || [],

      getRepetitions: (childId) => get().repetitions[childId] || {},
    }),
    { name: 'homeschool-diagnostic', version: 0, migrate: (state) => state },
  ),
);
